/**
 * Subscription Service
 * 
 * API client for subscription management endpoints.
 * Used by SettingsPage to open Polar customer portal.
 * 
 * See: POLAR_MANAGE.md for full documentation
 */

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.ninjacash.app';

export interface SubscriptionStatus {
  status: 'free' | 'trialing' | 'active' | 'past_due' | 'cancelled' | 'lifetime';
  plan: 'free' | 'monthly' | 'lifetime';
  trialEndDate: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  canUpgrade: boolean;
  canManage: boolean;
}

/**
 * Get current subscription status from API
 * Returns fresh data (fetches from Polar for active subscriptions)
 */
export async function getSubscriptionStatus(accessToken: string): Promise<SubscriptionStatus> {
  const response = await fetch(`${API_BASE_URL}/api/subscription/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `Failed to get subscription status: ${response.status}`);
  }

  return response.json();
}

/**
 * Create Polar customer portal session
 * Returns URL to redirect user to for subscription management
 */
export async function createPortalSession(accessToken: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/api/subscription/portal`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    
    if (response.status === 404) {
      throw new Error('No subscription found. You need an active subscription to manage billing.');
    }
    
    throw new Error(error.error || `Failed to create portal session: ${response.status}`);
  }

  const { portalUrl } = await response.json();
  
  if (!portalUrl) {
    throw new Error('No portal URL returned');
  }
  
  return portalUrl;
}

/**
 * Open Polar customer portal in new tab
 * Handles errors gracefully
 */
export async function openPortal(accessToken: string): Promise<{ success: boolean; error?: string }> {
  try {
    const portalUrl = await createPortalSession(accessToken);
    // Open in new tab with security flags (prevent tabnabbing)
    window.open(portalUrl, '_blank', 'noopener,noreferrer');
    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to open subscription portal';
    console.error('[Subscription] Portal error:', message);
    return { success: false, error: message };
  }
}
